import React, { useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import styles from '../post/Post.module.sass'
import AvatarLink from '../../../../components/profile/avatarLink/avatarLink'
import paths from '../../../../paths'
import CommentForm from '../commentForm/CommentForm'
import Comment from './../comment/Comment'
import moment from 'moment-mini'
import { useDispatch, useSelector } from 'react-redux'
import {
    fetchComments,
    likePost,
    likePostCancel,
    updateEphemeralPost
} from '../../../../store/ephemeral/ephemeralSlice'
import PaginationLoader from '../../../../components/ui/paginationLoader/PaginationLoader'
import { openMenu, setPageToReturnFromPayment } from '../../../../store/common/commonSlice'
import { RootState } from '../../../../store/rootReducer'
import Button from '../../../../components/ui/button'
import { isUserPro } from '../../../../utils/isUserPro'
import { useTranslation } from 'react-i18next'
import { Field, FieldProps, Form, Formik } from 'formik'
import { SubmitEphemeral } from '../../../../store/ephemeral/ephemeralTypings'

import RadioGroupField from '../../../../components/form/RadioGroupField'
import TextAreaField from '../../../../components/form/TextAreaField'
import { SelectOption } from '../../../../store/commonTypes'
import ReportPopup from '../reportPopup/ReportPopup'
import { getPlural } from '../../../../utils/localizationHelpers'
import { getLangFromPath } from '../../../../i18next'

import './../../../pm/materials/chatMenu.sass'
import UserMenu from '../userMenu/UserMenu'
import ReactPlayer from 'react-player'
import TemporaryMap_Show from '../newPostForm/ui/TemporaryMap_Show'

interface PostProps {
    isInPopup?: boolean
    post: any
    setIsFullPostOpen: React.Dispatch<boolean>
    replyCommentId: string | null
    setReplyCommentId: React.Dispatch<string | null>
    setIsLikeListOpen: React.Dispatch<boolean>
}

const visibilityOptions: SelectOption[] = [
    {
        value: 'public',
        label: 'Public'
    },
    {
        value: 'pro',
        label: 'Premium'
    }
]

const PostConstructor = ({
    isInPopup = false,
    post,
    setIsFullPostOpen,
    replyCommentId,
    setReplyCommentId,
    setIsLikeListOpen
}: PostProps) => {
    const [t] = useTranslation()

    const history = useHistory()
    const location = useLocation()
    const dispatch = useDispatch()

    const loggedIn = useSelector((state: RootState) => state.member.loggedIn)
    const user = useSelector((state: RootState) => state.member.user)

    const [isPostLiked, setIsPostLiked] = useState(post && post.likes ? post.likes.includes(post.id) : false)
    const [amountOfLikes, setAmountOfLikes] = useState<number>(post && post.like_count ? post.like_count : 0)
    const [minutesLeft, setMinutesLeft] = useState(0)
    const [timeRemainingStr, setTimeRemainingStr] = useState('')

    const [editingMode, setEditingMode] = React.useState(false)
    const [isReportPopupOpen, setIsreportPopupOpen] = React.useState(false)

    const [formErrorText, setFormErrorText] = React.useState('')

    useEffect(() => {
        if (post && post.likes) {
            setIsPostLiked(post.likes.includes(post.id))
        }
        if (post && post.like_count) {
            setAmountOfLikes(post.like_count)
        }
    }, [post])

    const handleLikePost = (e: { stopPropagation: () => void }) => {
        e.stopPropagation()
        if (!loggedIn) {
            dispatch(openMenu())
            return
        }

        if (isPostLiked) {
            dispatch(likePostCancel({ id: post.id, parent_id: post.id }))
            setIsPostLiked(false)
            setAmountOfLikes((prev) => (prev === 0 ? 0 : prev - 1))
        } else {
            dispatch(likePost({ id: post.id, parent_id: post.id }))
            setIsPostLiked(true)
            setAmountOfLikes((prev) => prev + 1)
        }
    }

    const getNextComments = () => {
        dispatch(fetchComments({ id: post.id, offset: post.currentLength }))
    }

    const [currentComments, setCurrentComments] = useState(post ? post.comments : [])
    const [additionalComments, setAdditionalComments] = useState([])

    useEffect(() => {
        if ((post && post.comments && post.comments.length > 0) || additionalComments.length > 0) {
            setCurrentComments(
                [...post.comments, ...additionalComments].filter(
                    (item, index, self) => index === self.findIndex((obj) => obj.id === item.id)
                )
            )
        } else {
            setCurrentComments([])
        }
    }, [post, additionalComments.length])

    useEffect(() => {
        const createdAt = post ? post.creation_time * 1000 : 0
        const expiresAt = createdAt + 24 * 60 * 60 * 1000
        const now = Date.now()
        const initialDiff = expiresAt - now

        if (initialDiff <= 0) {
            setTimeRemainingStr(`${t('Time remaining')}: 00:00`)
            setMinutesLeft(0)
            //@ts-ignore
            return
        }

        function updateTime() {
            const now = Date.now()
            const diffMs = expiresAt - now

            if (diffMs <= 0) {
                setTimeRemainingStr(`${t('Time remaining')}: 00:00`)
                setMinutesLeft(0)
                clearInterval(interval)
                return
            }

            const totalMinutes = Math.floor(diffMs / (1000 * 60))
            const hours = Math.floor(totalMinutes / 60)
            const minutes = totalMinutes % 60

            const pad = (n: number) => String(n).padStart(2, '0')

            setTimeRemainingStr(`${t('Time remaining')}: ${pad(hours)}:${pad(minutes)}`)
            setMinutesLeft(totalMinutes)
        }

        updateTime()
        const interval = setInterval(updateTime, 60 * 1000)

        return () => clearInterval(interval)
    }, [post])

    const [videoError, setVideoError] = useState(false)

    const imageSrc = useMemo(() => {
        if (!post || !post.status || post.status === 'hidden' || (isInPopup && minutesLeft === 0) || videoError) {
            return require('../../../../assets/404.png')
        }

        if (!post.attachments[0]) {
            return require('../../../../assets/404.png')
        }

        if (loggedIn) {
            const baseUrl = post.attachments[0].url_pro || post.attachments[0].url

            if (isUserPro(user.member_type)) {
                return `${baseUrl}&_=1`
            }

            if (post.creation_time && moment().diff(moment.unix(post.creation_time), 'minutes') > 61) {
                return `${baseUrl}&_=2`
            }

            return baseUrl
        }

        return post.attachments[0].url
    }, [minutesLeft, loggedIn, videoError])

    const videoSrc = useMemo(() => {
        if (!post || !post.status || post.status === 'hidden' || (isInPopup && minutesLeft === 0)) {
            return require('../../../../assets/404.png')
        }

        if (!post.attachments[0]) {
            return require('../../../../assets/404.png')
        }

        if (loggedIn) {
            const baseUrl = post.attachments[0].url_pro || post.attachments[0].url

            if (isUserPro(user.member_type) || user.id === post.user.id) {
                return `${baseUrl}&_=1`
            }

            if (post.creation_time && moment().diff(moment.unix(post.creation_time), 'minutes') > 61) {
                return `${post.attachments[0].thumbnail_url_pro}&_=2`
            }

            return baseUrl
        }

        return post.attachments[0].url
    }, [minutesLeft, loggedIn])

    const videoPreview = useMemo(() => {
        if (!post || !post.status || post.status === 'hidden' || (isInPopup && minutesLeft === 0)) {
            return require('../../../../assets/404.png')
        }

        if (!post.attachments[0]) {
            return require('../../../../assets/404.png')
        }

        if (loggedIn) {
            const baseUrl = post.attachments[0].thumbnail_url_pro

            if (isUserPro(user.member_type) || user.id === post.user.id) {
                return `${baseUrl}&_=1`
            }

            if (post.creation_time && moment().diff(moment.unix(post.creation_time), 'minutes') > 61) {
                return `${post.attachments[0].thumbnail_url_pro}&_=2`
            }

            return baseUrl
        }

        return post.attachments[0].thumbnail_url
    }, [minutesLeft, loggedIn])

    const [isReported, setIsReported] = useState(false)

    const handleContextMenu = (e: { preventDefault: () => void }) => {
        e.preventDefault()
    }

    const containerRef = React.useRef<HTMLDivElement>(null)

    const [showLoaction, setShowLoaction] = React.useState(false)

    // const geoLocationText = useMemo(() => {
    //     if (post && post.status !== 'hidden' && !loggedIn) return t('You need to be authorized to view the location')
    //     if (
    //         !user ||
    //         (post && post.status === 'pro' && !isUserPro(user.member_type)) ||
    //         (post &&
    //             post.status === 'public' &&
    //             !isUserPro(user.member_type) &&
    //             moment().diff(moment.unix(post.creation_time), 'minutes') > 61 &&
    //             user.id !== post.user.id)
    //     )
    //         return t('You need to become Premium member to view the location')
    //     return t('Show position')
    // }, [])

    // const geoLocationFunction = () => {
    //     if (showLoaction) {
    //         setShowLoaction(false)
    //         return
    //     }
    //
    //     if (post.status !== 'hidden' && !loggedIn) {
    //         dispatch(openMenu())
    //         return
    //     }
    //     if (
    //         (post.status === 'pro' && !isUserPro(user.member_type)) ||
    //         (post.status === 'public' &&
    //             !isUserPro(user.member_type) &&
    //             moment().diff(moment.unix(post.creation_time), 'minutes') > 61 &&
    //             user.id !== post.user.id)
    //     ) {
    //         history.push(paths.payment.path)
    //         dispatch(setPageToReturnFromPayment(location.pathname))
    //         return
    //     }
    //
    //     setShowLoaction(true)
    // }

    return (
        <div className={`${styles['comment']} ${isInPopup ? styles['commentPopup'] : ''}`} ref={containerRef}>
            {post ? (
                <>
                    <div className={styles['author']}>
                        <AvatarLink nick={post.user.nick} img={post.user.avatar_url} />
                        <Link to={paths.profile.toPath({ nick: post.user.nick })} className={styles['author']}>
                            {post.user.nick}
                        </Link>
                        {user && loggedIn && user.id !== post.user.id ? (
                            <div className={`${styles['avatar_menu']}`} style={{ marginTop: '4px' }}>
                                <img src={require('../../../../assets/ic-more-three-dots.svg')} />
                                <div className={`${styles['menu']} dotsMenu`}>
                                    <UserMenu post={post} />
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                        {post.status !== 'hidden' &&
                            loggedIn &&
                            isUserPro(user.member_type) &&
                            Object.prototype.hasOwnProperty.call(post, 'is_reviewed') &&
                            post.is_reviewed == 0 &&
                            (isReported ? (
                                <span style={{ marginLeft: 'auto', color: '#a23330', fontSize: '12px' }}>
                                    {t('Reported')}
                                </span>
                            ) : (
                                <img
                                    onClick={() => setIsreportPopupOpen(true)}
                                    src={require('../../../../assets/ic-block-red.svg')}
                                    style={{ marginLeft: 'auto', cursor: 'pointer', width: '20px' }}
                                />
                            ))}
                    </div>

                    {post.attachments.length > 0 &&
                    (minutesLeft > 0 || isInPopup) &&
                    (isInPopup || post.status !== 'hidden') ? (
                        <div
                            className={styles['img']}
                            style={{ overflow: 'hidden', cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (!isInPopup && post && post.attachments) {
                                    setIsFullPostOpen(true)
                                    history.push(location.pathname + '?post=' + post.id)
                                }
                            }}
                        >
                            {post && post.attachments && post.attachments[0].format.includes('video') ? (
                                <div
                                    className={styles['video_container']}
                                    key="video"
                                    style={{
                                        height: post.attachments[0].height
                                            ? Math.ceil(
                                                  ((containerRef.current.offsetWidth || 540) /
                                                      post.attachments[0].width) *
                                                      post.attachments[0].height
                                              ) + 'px'
                                            : '300px',
                                        maxHeight: 'calc(100vh - 150px)'
                                    }}
                                >
                                    {isInPopup && loggedIn ? (
                                        <ReactPlayer
                                            playing={loggedIn}
                                            // onClickPreview={(e) => e.stopPropagation()}
                                            onContextMenu={(e: React.FormEvent<HTMLInputElement>) => e.preventDefault()}
                                            onError={(e) => {
                                                console.log(e)
                                                setVideoError(true)
                                            }}
                                            controls
                                            stopOnUnmount={false}
                                            playsinline
                                            width="100%"
                                            height={
                                                Math.ceil(
                                                    ((containerRef.current.offsetWidth || 540) /
                                                        post.attachments[0].width) *
                                                        post.attachments[0].height
                                                ) + 'px'
                                            }
                                            url={videoSrc}
                                            // light={videoPreview}
                                            config={{ file: { attributes: { controlsList: 'nodownload' } } }}
                                            style={{ maxHeight: 'calc(100vh - 150px)' }}
                                        />
                                    ) : (
                                        <>
                                            <img src={videoPreview} style={{ width: '100%', height: '100%' }} />
                                            <img
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    cursor: 'pointer',
                                                    opacity: '0.6'
                                                }}
                                                src={require('../../../../assets/ic-play-black.svg')}
                                            />
                                        </>
                                    )}

                                    {videoError && (
                                        <>
                                            <img
                                                src={videoPreview || require('../../../../assets/404.png')}
                                                alt=""
                                                className={styles['errorPhoto']}
                                            />
                                            <img
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    cursor: 'pointer',
                                                    opacity: '0.6'
                                                }}
                                                src={require('../../../../assets/ic-play-black.svg')}
                                            />
                                        </>
                                    )}
                                </div>
                            ) : (
                                <img className={styles['photo']} src={imageSrc} onContextMenu={handleContextMenu} />
                            )}
                            {!user ||
                                (((post.status === 'pro' && !isUserPro(user.member_type)) ||
                                    (post.status === 'public' &&
                                        !isUserPro(user.member_type) &&
                                        moment().diff(moment.unix(post.creation_time), 'minutes') > 61)) &&
                                    user.id !== post.user.id && (
                                        <div
                                            className={`${styles['paymentDialog']} ${
                                                post && post.attachments && post.attachments[0].format.includes('video')
                                                    ? styles['paymentDialog_video']
                                                    : ''
                                            }`}
                                        >
                                            <p className="menuHeader">
                                                {t('You need to become Premium member to use this feature')}
                                            </p>
                                            <div className={styles['buttonLine']}>
                                                <Button
                                                    label={t('Become Premium')}
                                                    // @ts-ignore
                                                    onClick={(e: { stopPropagation: () => void }) => {
                                                        e.stopPropagation()
                                                        history.push(paths.payment.path)
                                                        dispatch(setPageToReturnFromPayment(location.pathname))
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                            {post && post.status !== 'hidden' && !loggedIn && (
                                <div
                                    className={`${styles['paymentDialog']} ${
                                        post && post.attachments && post.attachments[0].format.includes('video')
                                            ? styles['paymentDialog_video']
                                            : ''
                                    }`}
                                >
                                    <p className="menuHeader">{t('You need to be authorized to use this feature')}</p>
                                    <div className={styles['buttonLine']}>
                                        <Button
                                            label={'Log in'}
                                            // @ts-ignore
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                dispatch(openMenu())
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <span
                            onClick={() => {
                                if (!isInPopup) setIsFullPostOpen(true)
                                history.push(location.pathname + '?post=' + post.id)
                            }}
                            className={styles['photoDeleted']}
                        >
                            {t('Story deleted')}
                        </span>
                    )}

                    {post.content && !editingMode && (
                        <div
                            className={styles['text']}
                            onClick={() => {
                                if (!isInPopup) setIsFullPostOpen(true)
                                history.push(location.pathname + '?post=' + post.id)
                            }}
                        >
                            {post.content}
                        </div>
                    )}

                    {/*{post && post.status !== 'hidden' && post.geo && post.geo.lat ? (*/}
                    {/*    <div>*/}
                    {/*        <p*/}
                    {/*            style={{*/}
                    {/*                marginBottom: '20px',*/}
                    {/*                paddingLeft: '10px',*/}
                    {/*                display: 'flex',*/}
                    {/*                alignItems: 'center'*/}
                    {/*            }}*/}
                    {/*            onClick={geoLocationFunction}*/}
                    {/*        >*/}
                    {/*            <img src={require('../../../../assets/ic-my-position.svg')} />*/}
                    {/*            {showLoaction ? (*/}
                    {/*                <span*/}
                    {/*                    style={{*/}
                    {/*                        marginLeft: '5px',*/}
                    {/*                        color: 'var(--main-theme-color)',*/}
                    {/*                        textDecoration: 'underline',*/}
                    {/*                        cursor: 'pointer'*/}
                    {/*                    }}*/}
                    {/*                >*/}
                    {/*                    {t('Hide position')}*/}
                    {/*                </span>*/}
                    {/*            ) : (*/}
                    {/*                <span*/}
                    {/*                    style={{*/}
                    {/*                        marginLeft: '5px',*/}
                    {/*                        color: 'var(--main-theme-color)',*/}
                    {/*                        textDecoration: 'underline',*/}
                    {/*                        cursor: 'pointer'*/}
                    {/*                    }}*/}
                    {/*                >*/}
                    {/*                    {geoLocationText}*/}
                    {/*                </span>*/}
                    {/*            )}*/}
                    {/*        </p>*/}
                    {/*        {showLoaction && (*/}
                    {/*            <TemporaryMap_Show*/}
                    {/*                currentZoom={post.geo.zoom}*/}
                    {/*                currentCenter={{ lat: post.geo.lat, lon: post.geo.lon }}*/}
                    {/*                post={post}*/}
                    {/*            />*/}
                    {/*        )}*/}
                    {/*    </div>*/}
                    {/*) : (*/}
                    {/*    <></>*/}
                    {/*)}*/}

                    {editingMode && (
                        <Formik
                            initialValues={{
                                status: post.status || '',
                                content: post.content || ''
                            }}
                            onSubmit={async (values: SubmitEphemeral, { setSubmitting, setStatus }) => {
                                try {
                                    dispatch(updateEphemeralPost({ id: post.id, ...values }))
                                } catch (error) {
                                    setFormErrorText('Failed to update post. Please try again.')
                                } finally {
                                    setEditingMode(false)
                                    setSubmitting(false)
                                }
                            }}
                        >
                            {(props) => {
                                const {
                                    values: { image_data },
                                    setFieldValue,
                                    submitForm
                                } = props
                                return (
                                    <Form className={`${styles['form']} form`}>
                                        <p style={{ color: 'red', fontSize: '12px' }}>
                                            {t('The media in the ephemeral post cannot be changed')}
                                        </p>
                                        <Field name="status">
                                            {({ field, form }: FieldProps) => (
                                                <RadioGroupField
                                                    form={form}
                                                    field={field}
                                                    label={t('Upload in')}
                                                    options={visibilityOptions}
                                                />
                                            )}
                                        </Field>
                                        <Field name="content">
                                            {({ field, meta, form }: FieldProps) => (
                                                <TextAreaField
                                                    form={form}
                                                    field={field}
                                                    error={meta.error}
                                                    label={t('Description')}
                                                    placeholder={t('Photo description')}
                                                />
                                            )}
                                        </Field>
                                        <div className={styles['buttons']}>
                                            <Button
                                                tertiary
                                                type="button"
                                                label={t('Cancel')}
                                                onClick={() => setEditingMode(false)}
                                            />
                                            <Button label={t('Update')} onClick={submitForm} type="button" />
                                        </div>

                                        <p style={{ color: 'red', fontSize: '12px' }}>{formErrorText}</p>
                                    </Form>
                                )
                            }}
                        </Formik>
                    )}

                    <div
                        className={`${styles['stats']} ${
                            post && post.replies && post.replies.length > 0 ? styles['borderBottom'] : ''
                        }`}
                    >
                        <div className={styles['statsCounters']}>
                            {post && post.like_count > 0 && (
                                <div
                                    className={styles['likeList']}
                                    onClick={() => {
                                        if (isInPopup) {
                                            history.push(location.pathname + '?post=' + post.id + '&likes=' + post.id)
                                        } else {
                                            history.push(location.pathname + '?likes=' + post.id)
                                        }
                                        setIsLikeListOpen(true)
                                    }}
                                >
                                    {post.like_list && post.like_list.length > 0
                                        ? // @ts-ignore
                                          post.like_list.map((like) => (
                                              <div key={like.id} className={styles['avatar']}>
                                                  <img src={like.user.avatar_url} alt="User avatar" />
                                              </div>
                                          ))
                                        : Array.from({ length: amountOfLikes > 5 ? 5 : amountOfLikes || 0 }).map(
                                              (_, index) => (
                                                  <div key={`placeholder-${index}`} className={styles['avatar']}>
                                                      <img
                                                          src={require('../../../../assets/404.png')}
                                                          alt="Placeholder avatar"
                                                      />
                                                  </div>
                                              )
                                          )}
                                </div>
                            )}
                            <img
                                src={require('../../../../assets/ic-comment-2.svg')}
                                className={styles['comments_icon']}
                                onClick={() => {
                                    setIsFullPostOpen(true)
                                    history.push(location.pathname + '?post=' + post.id)
                                }}
                            />
                            <span>{post.comment_count || 0}</span>
                            <img
                                src={
                                    isPostLiked
                                        ? require('../../../../assets/ic-like-filled.svg')
                                        : require('../../../../assets/ic-like.svg')
                                }
                                style={{ width: '12px' }}
                                onClick={handleLikePost}
                            />
                            <span style={{ marginRight: '8px' }} onClick={handleLikePost}>
                                {amountOfLikes || 0}
                                {isInPopup && amountOfLikes && amountOfLikes > 0 ? (
                                    <span
                                        style={{
                                            color: 'var(--grey-medium-color)',
                                            textDecoration: 'underline',
                                            marginLeft: '4px'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            history.push(location.pathname + '?post=' + post.id + '&likes=' + post.id)
                                            setIsLikeListOpen(true)
                                        }}
                                    >
                                        {t('persons liked this story', {
                                            postProcess: 'interval',
                                            count: getPlural(amountOfLikes, getLangFromPath(location.pathname))
                                        })}
                                    </span>
                                ) : null}
                            </span>
                        </div>

                        {user &&
                            post.user.id === user.id &&
                            post.status !== 'hidden' &&
                            post.editing_expiration &&
                            moment().isBefore(moment(post.editing_expiration * 1000)) &&
                            (editingMode ? (
                                <></>
                            ) : (
                                <span onClick={() => setEditingMode((prev) => !prev)} className={styles['editButton']}>
                                    {t('Edit post')}
                                </span>
                            ))}
                        <div className={styles['time']}>
                            {post.status === 'hidden' || minutesLeft == 0
                                ? moment(post.creation_time * 1000).format('DD.MM.YYYY - HH:mm')
                                : timeRemainingStr}
                        </div>
                    </div>

                    {currentComments.length > 0 && (
                        <div className={styles['replies']}>
                            {/*@ts-ignore*/}
                            {currentComments.map((re, index) => (
                                <React.Fragment key={re.id}>
                                    <Comment
                                        post={post}
                                        comment={re}
                                        setIsFullPostOpen={setIsFullPostOpen}
                                        isInPopup={isInPopup}
                                        setReplyCommentId={setReplyCommentId}
                                        replyCommentId={replyCommentId}
                                        last={index + 1 === currentComments.length}
                                    />
                                </React.Fragment>
                            ))}
                            {post.commentsLoading && <PaginationLoader />}
                            {post.hasMoreComments && !post.commentsLoading && (
                                <div
                                    style={{ color: 'var(--main-theme-color)', cursor: 'pointer', fontWeight: 'bold' }}
                                    onClick={getNextComments}
                                >
                                    {t('Load more')}
                                </div>
                            )}
                        </div>
                    )}

                    {post.status !== 'hidden' && minutesLeft > 0 && (
                        <CommentForm post={post} setAdditionalComments={setAdditionalComments} />
                    )}
                </>
            ) : null}
            {isReportPopupOpen && (
                <ReportPopup onClose={() => setIsreportPopupOpen(false)} id={post.id} setIsReported={setIsReported} />
            )}
        </div>
    )
}

export default PostConstructor
